import { ViteReactSSG } from 'vite-react-ssg'
import * as Sentry from '@sentry/react'
import { HelmetProvider } from 'react-helmet-async'
import '@fontsource-variable/inter'
import '@fontsource-variable/fraunces'
import App from './App'
import { tryReloadOnChunkError } from './lib/chunk-reload'
import './index.css'

// Helper para defer a requestIdleCallback com fallback setTimeout
const deferToIdle = (cb) => {
  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    window.requestIdleCallback(cb, { timeout: 2000 })
  } else if (typeof window !== 'undefined') {
    setTimeout(cb, 0)
  }
}

// Rede de segurança síncrona contra falha de carregamento de chunk lazy.
// Um import() que rejeita pode chegar como `error` (script) ou
// `unhandledrejection` (promise) — cobrimos ambos. tryReloadOnChunkError só
// recarrega se a mensagem casar com um padrão de chunk error e respeita o
// cooldown, então é seguro coexistir com os handlers do telemetry.
function installChunkReloadGuard() {
  if (typeof window === 'undefined') return
  window.addEventListener('error', (event) => {
    const err =
      event.error instanceof Error ? event.error : new Error(event.message || 'Script error')
    tryReloadOnChunkError(err)
  })
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    const err = reason instanceof Error ? reason : new Error(String(reason ?? 'Unhandled rejection'))
    tryReloadOnChunkError(err)
  })
}

// Sentry — inicialização síncrona para capturar erros de hidratação (janela 0–2s).
// Guard: só roda em browser real (isClient check no call site).
function initSentry() {
  if (!import.meta.env.VITE_SENTRY_DSN) return

  const ALLOWED_REQUEST_HEADERS = new Set(['content-type', 'user-agent', 'accept'])

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.2,
    release: import.meta.env.VITE_APP_VERSION ?? 'dev',
    integrations: [Sentry.browserTracingIntegration()],
    sendDefaultPii: false,
    // Ruído de webviews/navegadores in-app (Instagram, Facebook, etc.) que
    // injetam scripts próprios — não é erro do app. Filtra por mensagem.
    ignoreErrors: [
      'Java object is gone',
      'Error invoking postMessage',
      'Object Not Found Matching Id',
    ],
    beforeSend(event) {
      const frames = event.exception?.values?.[0]?.stacktrace?.frames
      // Extensões de browser e scripts injetados por webviews in-app (iabjs://
      // do Instagram/Facebook) não são código nosso — descartar.
      if (frames?.some((f) => /extension:\/\/|iabjs:\/\//.test(f.filename ?? ''))) {
        return null
      }
      if (event.request) {
        delete event.request.cookies
        delete event.request.data
        if (event.request.headers) {
          event.request.headers = Object.fromEntries(
            Object.entries(event.request.headers).filter(([k]) =>
              ALLOWED_REQUEST_HEADERS.has(k.toLowerCase())
            )
          )
        }
      }
      if (event.user) {
        event.user = { id: event.user.id }
      }
      return event
    },
    beforeBreadcrumb(breadcrumb) {
      if ((breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') && breadcrumb.data) {
        delete breadcrumb.data.request_body
        delete breadcrumb.data.response_body
      }
      return breadcrumb
    },
  })
}

// PostHog analytics + UTMs + super properties — deferido para idle (não-crítico para LCP).
// installGlobalErrorHandlers foi extraído para dynamic import síncrono no call site.
async function initAnalytics() {
  const [{ default: analytics }, { captureUtms, getLastTouchUtms }, { captureTikTokAttribution }] =
    await Promise.all([
      import('./lib/analytics'),
      import('./lib/analytics-utm'),
      import('./lib/pixel'),
    ])

  analytics.init()
  captureUtms()
  captureTikTokAttribution()
  analytics.setSuperProperty('environment', import.meta.env.MODE)
  analytics.setSuperProperty('app_version', import.meta.env.VITE_APP_VERSION ?? 'dev')

  function detectDeviceType() {
    if (typeof window === 'undefined') return 'unknown'
    const ua = navigator.userAgent || ''
    if (/Mobi|Android|iPhone|iPod/i.test(ua)) return 'mobile'
    if (/iPad|Tablet/i.test(ua)) return 'tablet'
    const hasCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false
    const hasFinePointer = window.matchMedia?.('(pointer: fine)').matches ?? false
    if (hasCoarsePointer && !hasFinePointer) {
      return window.innerWidth < 768 ? 'mobile' : 'tablet'
    }
    return 'desktop'
  }
  analytics.setSuperProperty('device_type', detectDeviceType())

  const lastTouchUtms = getLastTouchUtms()
  for (const [key, value] of Object.entries(lastTouchUtms)) {
    analytics.setSuperProperty(`utm_last_${key.replace('utm_', '')}`, value)
  }
}

const Fallback = () => (
  <div style={{ padding: '2rem', fontFamily: 'Inter, sans-serif' }}>
    Ocorreu um erro inesperado. Recarregue a página e tente novamente.
  </div>
)

// ViteReactSSG: exporta createRoot para o CLI do plugin gerenciar hidratação e SSG.
// O App.tsx usa Routes internamente, então montamos ele sob path '*' para o router
// interno funcionar. O SSG pré-renderiza apenas '/' (configurado em vite.config.js
// via includedRoutes).
//
// Ordem de inicialização no client:
//   1. initSentry() — síncrono: ErrorBoundary + window.onerror desde t=0.
//   2. import('./lib/telemetry').then(installGlobalErrorHandlers) — microtask (~1ms).
//   3. deferToIdle(initAnalytics) — deferido: PostHog não bloqueia LCP.
export const createRoot = ViteReactSSG(
  {
    routes: [
      {
        path: '*',
        element: (
          <HelmetProvider>
            <Sentry.ErrorBoundary
              fallback={<Fallback />}
              onError={(error) => tryReloadOnChunkError(error)}
            >
              <App />
            </Sentry.ErrorBoundary>
          </HelmetProvider>
        ),
      },
    ],
  },
  ({ isClient }) => {
    if (!isClient) return
    // Síncrono — captura erros de hidratação (janela crítica 0–2s).
    initSentry()
    // Síncrono — rede de auto-reload para falha de import() de chunk lazy
    // (iOS Safari: "Importing a module script failed"). Registrado aqui, sem
    // depender do import('./lib/telemetry') que pode chegar tarde ou, pior, ter
    // o próprio chunk falhando no mesmo cenário de rede/webview. tryReloadOnChunkError
    // já está importado no topo e é idempotente (cooldown via sessionStorage).
    installChunkReloadGuard()
    // Dynamic import: resolve em microtask (~1ms), muito mais rápido que idle.
    import('./lib/telemetry').then(({ installGlobalErrorHandlers }) => installGlobalErrorHandlers())
    // Deferido — não-crítico para LCP.
    deferToIdle(initAnalytics)
  }
)
